import { ReactElement, ChangeEvent, useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { pick } from "ramda";
import { Row } from "@tanstack/react-table";
import { t } from "@lingui/core/macro";

import { Button } from "_/components/button";
import { DateTime } from "_/components/date-time";
import { SubmitCancelButtons, useModal } from "_/components/modal";
import { ColumnDef, Table } from "_/components/table";
import { TextField, FormTextField } from "_/components/text-field";
import { FormSelect, Option } from "_/components/select";
import { UuidTooltip } from "_/components/uuid-tooltip";
import { FormField } from "_/components/form-field";
import { MenuItem, OverlayMenu } from "_/components/overlay-menu";
import { Icon } from "_/components/icon";
import { RadioButton, RadioGroup } from "_/components/radio";
import { useAddToast } from "_/components/toasts";
import { PermissionTest, testPermissions } from "_/components/permission-test";
import { Form } from "_/components/form";

import {
  useMaterials,
  Material,
  Manufacturer,
  useManufacturers,
  useCreateMaterial,
  useUpdateMaterial,
  NewMaterial,
  MaterialUpdate,
  NewManufacturer,
  ManufacturerUpdate,
  useCreateManufacturer,
  useUpdateManufacturer,
} from "_/data/materials";
import { ROOT_DOMAIN, useCurrentUserPermissions } from "_/data/rbac";
import { Uuid } from "_/types";
import { allDefined } from "_/utils";

import { AdminPage } from "./admin-page";

import * as S from "./styled";

/**
 * Table for displaying all materials manufacturers.
 */
const ManufacturersTable = ({
  actions,
}: {
  actions?: (machine: Manufacturer) => MenuItem[];
}) => {
  const { data: manufacturers = [], isLoading } = useManufacturers();

  const columns: ColumnDef<Manufacturer>[] = [
    {
      id: "name",
      header: t`common.name`,
      accessorKey: "name",
      cell: ({ row: { original: rowData } }) => (
        // The ID here is to enable linking from the materials table.
        <div id={rowData.id}>{rowData.name}</div>
      ),
    },
    {
      id: "website",
      header: t`common.website`,
      accessorKey: "website",
      cell: ({ row: { original: rowData } }) => (
        <a href={rowData.website} rel="noreferrer" target="_blank">
          {rowData.website}
        </a>
      ),
    },
    {
      id: "id",
      header: t`common.id`,
      accessorKey: "id",
      cell: ({ row: { original: rowData } }) => (
        <UuidTooltip uuid={rowData.id} />
      ),
    },
  ];

  if (actions) {
    columns.push({
      id: "actions",
      header: t`common.actions`,
      cell: ({ row: { original: rowData } }) => (
        <OverlayMenu
          target={
            <Button kind="secondary" size="small" iconOnly>
              <Icon variant="MoreVert" />
            </Button>
          }
          placement="bottomStart"
          items={actions(rowData)}
        />
      ),
    });
  }

  return (
    <Table
      columns={columns}
      data={manufacturers}
      loading={isLoading}
      initialState={{ sorting: [{ id: "name", desc: false }] }}
    />
  );
};

/**
 * Custom validation function to check that the url starts with `https://`.
 */
const validateHttpsUrl = (value: string) => {
  const httpsPattern = /^https:\/\/.+$/;

  if (!httpsPattern.test(value)) {
    return t`components.admin-view.url-must-be-https`;
  }

  if (!URL.canParse(value)) {
    return t`components.admin-view.url-invalid`;
  }

  return true;
};

/**
 * Form for creating or editing a manufacturer record.
 *
 * If the `manufacturer` prop is provided, the form can be used for updating
 * that manufacturer record, otherwise a new manufacturer will be created.
 */
const ManufacturerEditForm = ({
  manufacturer,
  onClose,
}: {
  manufacturer?: Manufacturer;
  onClose: () => void;
}) => {
  const createManufacturer = useCreateManufacturer();
  const updateManufacturer = useUpdateManufacturer();
  const addToast = useAddToast();

  const defaultValues = manufacturer || { name: "", website: "" };

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({ defaultValues });

  const onSubmit = handleSubmit(async (data) => {
    const { name, website } = data;

    if (manufacturer) {
      const update: ManufacturerUpdate = {
        name,
        website,
      };

      await updateManufacturer.mutateAsync({
        id: manufacturer?.id,
        update,
        onError: (code?: number) => {
          const title =
            code === 403
              ? t`components.admin-view.action-permission-denied`
              : t`components.admin-view.manufacturer-update-failed`;

          addToast({
            title,
            kind: "warning",
            timeout: 5_000,
          });
        },
      });
    } else if (name && website) {
      const newManufacturer: NewManufacturer = {
        name,
        website,
      };

      await createManufacturer.mutateAsync({
        newManufacturer,
        onError: (code?: number) => {
          const title =
            code === 403
              ? t`components.admin-view.action-permission-denied`
              : t`components.admin-view.manufacturer-creation-failed`;

          addToast({
            title,
            kind: "warning",
            timeout: 5_000,
          });
        },
      });
    }

    onClose?.();
  });

  return (
    <Form onSubmit={onSubmit}>
      <FormTextField
        control={control}
        name="name"
        label={t`common.name`}
        rules={{ required: t`components.admin-view.name-required` }}
        placeholder={t`components.admin-view.manufacturer-name`}
      />
      <FormTextField
        control={control}
        name="website"
        label={t`common.website`}
        rules={{
          validate: validateHttpsUrl,
          required: t`components.admin-view.name-required`,
        }}
        placeholder={t`common.website`}
      />
      <SubmitCancelButtons onCancel={onClose} disableSubmit={!isDirty} />
    </Form>
  );
};

// If the `material` prop is provided, use the form for updating that material record.
// Otherwise, create a new material.
const MaterialEditForm = ({
  material,
  onClose,
}: {
  material?: Material;
  onClose: () => void;
}) => {
  const colorPickerRef = useRef<HTMLInputElement>(null);
  const createMaterial = useCreateMaterial();
  const updateMaterial = useUpdateMaterial();
  const addToast = useAddToast();
  const { data: manufacturers } = useManufacturers();

  const fields = [
    "name",
    "polymer",
    "color",
    "pigmented",
    "capabilities",
    "spoolSizes",
    "glassTransitionTemp",
    "defaultExtrusionTemp",
    "minExtrusionTemp",
    "maxExtrusionTemp",
  ] as const;

  type FormData = Omit<MaterialUpdate, "archived">;

  const defaultValues: FormData = material
    ? {
        ...pick(fields, material),
        manufacturerId: material.manufacturer.id,
      }
    : {
        color: "#000000",
        pigmented: false,
        spoolSizes: [],
        capabilities: { simulation: false, optimization: false },
      };

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({ defaultValues });

  const onSubmit = handleSubmit(async (data: FormData) => {
    if (material) {
      await updateMaterial.mutateAsync({
        id: material?.id,
        update: data,
        onError: (code?: number) => {
          const title =
            code === 403
              ? t`components.admin-view.action-permission-denied`
              : t`components.admin-view.material-update-failed`;

          addToast({
            title,
            kind: "warning",
            timeout: 5_000,
          });
        },
      });
    } else {
      // This is purely a typeguard to appease typescript. The values from the
      // form should all be defined at this point as the form validation will
      // have ensured this.
      if (!allDefined<FormData>(data)) {
        throw new Error(
          "Required fields from the form are missing in the submission data."
        );
      }

      const newMaterial: NewMaterial = {
        ...data,
        domainId: ROOT_DOMAIN,
      };

      await createMaterial.mutateAsync({
        newMaterial,
        onError: (code?: number) => {
          const title =
            code === 403
              ? t`components.admin-view.action-permission-denied`
              : t`components.admin-view.material-creation-failed`;
          addToast({
            title,
            kind: "warning",
            timeout: 5_000,
          });
        },
      });
    }

    onClose();
  });

  const pigmentOptions: Option<boolean>[] = [
    {
      label: t`components.admin-view.natural`,
      value: "natural",
      data: false,
    },
    {
      label: t`components.admin-view.pigmented`,
      value: "pigmented",
      data: true,
    },
  ];

  const manufacturerOptions: Option<Uuid>[] = useMemo(
    () => manufacturers?.map((mf) => ({ label: mf.name, value: mf.id })) ?? [],
    [manufacturers]
  );

  const optimizationOpts = [
    {
      label: t`common.not-optimizable`,
      value: "notOptimizable",
      data: { simulation: false, optimization: false },
    },
    {
      label: t`common.optimizable`,
      value: "optimizable",
      data: { simulation: true, optimization: true },
    },
  ];

  return (
    <Form onSubmit={onSubmit}>
      <FormTextField
        control={control}
        name="name"
        label={t`common.name`}
        rules={{ required: t`components.admin-view.name-required` }}
        placeholder={t`components.admin-view.material-name`}
      />
      <FormSelect
        control={control}
        name="manufacturerId"
        label={t`common.manufacturer`}
        options={manufacturerOptions}
        rules={{ required: t`components.admin-view.manufacturer-required` }}
      />
      <FormTextField
        control={control}
        name="polymer"
        label={t`common.polymer`}
        placeholder={t`common.polymer-placeholder`}
        rules={{ required: t`form.field-value-required` }}
      />
      <S.SideBySide>
        <Controller
          control={control}
          name="color"
          render={({ field, fieldState }) => (
            <FormField
              label={t`common.color`}
              hint={fieldState.error?.message}
              invalid={fieldState.invalid}
            >
              <S.MaterialColorFormFieldContainer
                onClick={() => colorPickerRef.current?.click()}
              >
                <S.MaterialColorContainer>
                  <S.MaterialColorBox $color={field.value || "#000000"} />
                  {field.value}
                  <input
                    {...field}
                    type="color"
                    ref={colorPickerRef}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e.target.value.toUpperCase());
                    }}
                  />
                </S.MaterialColorContainer>
              </S.MaterialColorFormFieldContainer>
            </FormField>
          )}
        />
        <FormSelect
          control={control}
          name="pigmented"
          label={t`common.pigmented`}
          options={pigmentOptions}
          isSearchable={false}
        />
      </S.SideBySide>
      <Controller
        control={control}
        name="spoolSizes"
        rules={{ required: t`form.field-value-required` }}
        render={({ field, fieldState }) => (
          <FormField
            label={t`common.spool-sizes`}
            hint={fieldState.error?.message}
            invalid={fieldState.invalid}
          >
            <TextField
              {...field}
              placeholder={t`common.spool-sizes-placeholder`}
              value={field.value?.join(" ")}
              onChange={(v: string) => {
                const values = v.split(" ");
                field.onChange(values);
              }}
              onBlur={() => {
                // Strip away any invalid values.
                const values = field.value
                  ?.map((v) => parseInt(v.toString()))
                  .filter((v) => !isNaN(v));
                field.onChange(values);
              }}
            />
          </FormField>
        )}
      />
      <Controller
        control={control}
        name="capabilities"
        rules={{ required: t`components.admin-view.capabilities-required` }}
        render={({ field, fieldState }) => {
          const value = field.value?.optimization ? 1 : 0;
          const onChange = (v: string) => {
            return field.onChange(optimizationOpts[Number(v)].data);
          };
          const options = optimizationOpts.map((o, i) => (
            <RadioButton key={i} label={o.label} value={i} />
          ));

          return (
            <FormField
              label={t`common.capabilities`}
              hint={fieldState.error?.message}
              invalid={fieldState.invalid}
            >
              <RadioGroup
                {...field}
                value={value.toString()}
                onChange={onChange}
                row
              >
                {options}
              </RadioGroup>
            </FormField>
          );
        }}
      />
      <S.SideBySide>
        <FormTextField
          control={control}
          name="glassTransitionTemp"
          type="number"
          label={t`common.tg`}
          placeholder="°C"
          rules={{
            required: t`form.field-value-required`,
            min: { value: -273, message: t`form.min-temp-error ${-273}` },
          }}
        />
        <FormTextField
          control={control}
          name="defaultExtrusionTemp"
          type="number"
          label={t`common.extrusion-temp`}
          placeholder="°C"
          rules={{
            required: t`form.field-value-required`,
            min: { value: 0, message: t`form.min-temp-error ${0}` },
          }}
        />
        <FormTextField
          control={control}
          name="minExtrusionTemp"
          type="number"
          label={t`common.min-extrusion-temp`}
          placeholder="°C"
          rules={{
            required: t`form.field-value-required`,
            min: { value: 0, message: t`form.min-temp-error ${0}` },
          }}
        />
        <FormTextField
          control={control}
          name="maxExtrusionTemp"
          type="number"
          label={t`common.max-extrusion-temp`}
          placeholder="°C"
          rules={{
            required: t`form.field-value-required`,
            min: { value: 0, message: t`form.min-temp-error ${0}` },
          }}
        />
      </S.SideBySide>
      <SubmitCancelButtons onCancel={onClose} disableSubmit={!isDirty} />
    </Form>
  );
};

const getRowCapabilityNumberValue = (row: Row<Material>) => {
  return row.original.capabilities.optimization ? 1 : 0;
};

// Column definitions shared between the Materials and Archived Materials tables.
// Formatted as a function that returns the array of `ColumnDef`s so that the `lingui`
// macro will continue to work as expected.
const sharedMaterialTableColumns: () => ColumnDef<Material>[] = () => [
  {
    id: "name",
    accessorKey: "name",
    header: t`common.name`,
    cell: ({ row: { original: rowData } }) => rowData.name,
  },
  {
    id: "color",
    accessorKey: "color",
    header: t`common.color`,
    cell: ({ row: { original: rowData } }) => (
      <S.MaterialColorContainer>
        <S.MaterialColorBox $color={rowData.color} />
        {rowData.color.toUpperCase()}
      </S.MaterialColorContainer>
    ),
  },
  {
    id: "pigmented",
    accessorKey: "pigmented",
    header: t`common.pigmented`,
    cell: ({ cell }) => cell.getValue() && <S.Checkmark>✓</S.Checkmark>,
  },
  {
    id: "manufacturer",
    header: t`common.manufacturer`,
    accessorKey: "manufacturer.name",
    cell: ({ row: { original: rowData } }) => (
      <a href={`#${rowData.manufacturer.id}`}>{rowData.manufacturer.name}</a>
    ),
  },
  {
    id: "optimizable",
    header: t`common.optimizable`,
    accessorKey: "capabilities",
    sortingFn: (rowA, rowB) => {
      return (
        getRowCapabilityNumberValue(rowB) - getRowCapabilityNumberValue(rowA)
      );
    },
    cell: ({ row: { original: rowData } }) => {
      if (!rowData.capabilities.optimization) return null;
      return <S.Checkmark>✓</S.Checkmark>;
    },
  },
  {
    id: "id",
    header: t`common.id`,
    accessorKey: "id",
    cell: ({ row: { original: rowData } }) => <UuidTooltip uuid={rowData.id} />,
  },
];

const ArchivedMaterials = ({
  actions,
}: {
  actions: (material: Material) => MenuItem[];
}) => {
  const { data: materials, isLoading } = useMaterials();

  const archivedMaterials = (materials ?? []).filter(
    (material) => material.archived
  );

  const columns: ColumnDef<Material>[] = [
    ...sharedMaterialTableColumns(),
    {
      id: "archivedAt",
      header: t`common.archived-at`,
      accessorKey: "updatedAt",
      cell: ({ row: { original: rowData } }) => (
        <DateTime value={rowData.updatedAt} />
      ),
    },
    {
      id: "actions",
      header: t`common.actions`,
      cell: ({ row: { original: rowData } }) => (
        <OverlayMenu
          target={
            <Button kind="secondary" size="small" iconOnly>
              <Icon variant="MoreVert" />
            </Button>
          }
          placement="bottomStart"
          items={actions(rowData)}
        />
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      data={archivedMaterials}
      loading={isLoading}
      initialState={{ sorting: [{ id: "name", desc: false }] }}
    />
  );
};

const ArchiveMaterialForm = ({
  material,
  onClose,
}: {
  material: Material;
  onClose: () => void;
}) => {
  const updateMaterial = useUpdateMaterial();
  const addToast = useAddToast();

  async function onSubmit() {
    await updateMaterial.mutateAsync({
      id: material.id,
      update: { archived: true },
      onError: (code?: number) => {
        const title =
          code === 403
            ? t`components.admin-view.action-permission-denied`
            : t`components.admin-view.material-archiving-failed`;

        addToast({
          title,
          kind: "warning",
          timeout: 5_000,
        });
      },
    });

    onClose?.();
  }

  return (
    <>
      <S.ArchiveMessage>
        {t`components.admin-view.confirm-archive-material`}
      </S.ArchiveMessage>
      <SubmitCancelButtons
        onCancel={onClose}
        onSubmit={onSubmit}
        submitButtonLabel={t`common.archive`}
        disableSubmit={false}
      />
    </>
  );
};

export const Materials = (): ReactElement => {
  const materialsModal = useModal<Material>();
  const archiveMaterialsModal = useModal<Material>();
  const manufacturersModal = useModal<Manufacturer>();

  const { data: materials, isLoading: materialsLoading } = useMaterials();
  const { data: rootPermissions } = useCurrentUserPermissions(ROOT_DOMAIN);
  const updateMaterial = useUpdateMaterial();
  const addToast = useAddToast();

  const availableMaterials =
    materials?.filter((material) => !material.archived) ?? [];

  const canEditMaterials = testPermissions("materials/write", rootPermissions);

  const canEditManufacturers = testPermissions(
    "manufacturers/write",
    rootPermissions
  );

  const materialActions = (material: Material) =>
    canEditMaterials
      ? [
          {
            render: t`common.edit`,
            onClick: () => materialsModal.openModal(material),
          },
          {
            render: t`common.archive`,
            onClick: () => archiveMaterialsModal.openModal(material),
          },
        ]
      : [];

  const archivedMaterialActions = (material: Material) =>
    canEditMaterials
      ? [
          {
            render: t`common.unarchive`,
            onClick: () => {
              updateMaterial.mutate({
                id: material.id,
                update: { archived: false },
                onError: (code?: number) => {
                  const title =
                    code === 403
                      ? t`components.admin-view.action-permission-denied`
                      : t`components.admin-view.material-unarchiving-failed`;

                  addToast({
                    title,
                    kind: "warning",
                    timeout: 5_000,
                  });
                },
              });
            },
          },
        ]
      : [];

  const manufacturerActions = (manufacturer: Manufacturer) =>
    canEditManufacturers
      ? [
          {
            render: t`common.edit`,
            onClick: () => manufacturersModal.openModal(manufacturer),
          },
        ]
      : [];

  const columns: ColumnDef<Material>[] = [
    ...sharedMaterialTableColumns(),
    {
      id: "actions",
      header: t`common.actions`,
      cell: ({ row: { original: rowData } }) => (
        <OverlayMenu
          target={
            <Button kind="secondary" size="small" iconOnly>
              <Icon variant="MoreVert" />
            </Button>
          }
          placement="bottomStart"
          items={materialActions(rowData)}
        />
      ),
    },
  ];

  const jsonDownloadUrl = "/api/v0/materials/json";

  return (
    <AdminPage title={t`common.materials`}>
      <S.AdminContentSection>
        <S.AdminContentSectionTitleContainer>
          <h2 />
          <S.AdminContentSectionTitleButtonContainer>
            <PermissionTest
              domainId={ROOT_DOMAIN}
              requiredPermissions={"materials/read"}
              render={({ allowed }) => (
                <a href={jsonDownloadUrl}>
                  <Button kind="secondary" disabled={!allowed}>
                    <Icon variant="FileDownload" />
                    {t`components.admin-view.download-json`}
                  </Button>
                </a>
              )}
            />
            <PermissionTest
              domainId={ROOT_DOMAIN}
              requiredPermissions={"materials/create"}
              render={({ allowed }) => (
                <Button
                  onClick={() =>
                    materialsModal.openModal(materialsModal.modalData)
                  }
                  disabled={!allowed}
                >{t`common.create`}</Button>
              )}
            />
          </S.AdminContentSectionTitleButtonContainer>
        </S.AdminContentSectionTitleContainer>
        <Table
          columns={columns}
          data={availableMaterials}
          loading={materialsLoading}
          initialState={{ sorting: [{ id: "name", desc: false }] }}
        />
      </S.AdminContentSection>
      <S.AdminContentSection>
        <S.AdminContentSectionTitleContainer>
          <h2>{t`common.manufacturers`}</h2>
          <PermissionTest
            domainId={ROOT_DOMAIN}
            requiredPermissions={"manufacturers/create"}
            render={({ allowed }) => (
              <Button
                onClick={() =>
                  manufacturersModal.openModal(manufacturersModal.modalData)
                }
                disabled={!allowed}
              >{t`common.create`}</Button>
            )}
          />
        </S.AdminContentSectionTitleContainer>
        <ManufacturersTable actions={manufacturerActions} />
      </S.AdminContentSection>
      <S.AdminContentSection>
        <S.AdminContentSectionTitleContainer>
          <h2>{t`components.admin-view.archived-materials`}</h2>
        </S.AdminContentSectionTitleContainer>
        <ArchivedMaterials actions={archivedMaterialActions} />
      </S.AdminContentSection>
      <archiveMaterialsModal.Modal
        title={t`components.admin-view.archive-material`}
      >
        {archiveMaterialsModal.isModalOpen &&
          archiveMaterialsModal.modalData && (
            <ArchiveMaterialForm
              material={archiveMaterialsModal.modalData}
              onClose={archiveMaterialsModal.closeModal}
            />
          )}
      </archiveMaterialsModal.Modal>
      <materialsModal.Modal
        title={
          materialsModal.modalData
            ? t`components.admin-view.edit-material`
            : t`components.admin-view.create-material`
        }
      >
        {materialsModal.isModalOpen && (
          <MaterialEditForm
            material={materialsModal.modalData}
            onClose={materialsModal.closeModal}
          />
        )}
      </materialsModal.Modal>
      <manufacturersModal.Modal
        title={
          manufacturersModal.modalData
            ? t`components.admin-view.edit-manufacturer`
            : t`components.admin-view.create-manufacturer`
        }
      >
        {manufacturersModal.isModalOpen && (
          <ManufacturerEditForm
            manufacturer={manufacturersModal.modalData}
            onClose={manufacturersModal.closeModal}
          />
        )}
      </manufacturersModal.Modal>
    </AdminPage>
  );
};
